import { CiCircleCheck } from "react-icons/ci";
import { PiHourglassSimpleLowFill } from "react-icons/pi";
import { TiWarningOutline } from "react-icons/ti";
import { StylesConfig } from "react-select";
import { toast } from "sonner";

import {
  TrackingCategoryNestedSelectOption,
  TrackingCategoryOption
} from "@/jenesys-types/data/common";
import { IAccountingDataStatus } from "@/jenesys-types/jenesys-entities/config";

import theme from "@/components/theme";
import { isFeatureEnabled } from "@/util/environments";

/**
 * This function formats a number or string with a thousands separator of space.
 * This uses French locale to format the number, as it uses a space as the thousands separator natively.
 * If the input cannot be converted to a valid number, the function returns null.
 * @param {string | number} value - The value to be formatted.
 * @returns {string | null} The function `formatNumberWithThousandsSeparator` returns a string that represents the number
 * in the French locale format with a space as the thousands separator and a period as the decimal separator.
 * If the input is not a valid number, it returns null.
 *
 * French currency formatting is the Intl standard for formatting numbers with a space as the thousands separator.
 * @example formatNumberWithThousandsSeparator(1234.56) // "1 234.56"
 * @example formatNumberWithThousandsSeparator(1234567.89) // "1 234 567.89"
 * @example formatNumberWithThousandsSeparator("1234.56") // "1 234.56"
 * @example formatNumberWithThousandsSeparator("") // null
 */
export const formatNumberWithThousandsSeparator = (
  value: string | number
): string | null => {
  // Convert the input to a number
  const numberValue = Number(value);

  // Check if the conversion results in NaN
  if (Number.isNaN(numberValue)) {
    return null;
  }

  // Number format for French currency returns a string with a comma as the decimal separator, and a space as the thousands separator, e.g. 1 234,56
  // However, we're returning a period instead of a comma here.
  const formattedNumber = new Intl.NumberFormat("fr-FR", {
    useGrouping: true,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numberValue);
  return formattedNumber.replace(",", ".");
};

const getErrorMessage = (error: any): string => {
  const errorMessage =
    error?.data?.message || error?.data?.detail || error?.message;

  if (typeof errorMessage === "string") return errorMessage;
  return "An error has occurred";
};

const handleErrorType = (error: any): string => {
  if (error?.status === 401) {
    return "Unauthorized access. Please [login](/login) to continue";
  }
  if (error?.status === 403) {
    return "Insufficient permissions. Please contact your workspace manager";
  }
  return getErrorMessage(error);
};

export const toastError = (
  title?: string | null,
  error?: any,
  description?: string,
  stay?: boolean
) => {
  // Build error description with a link to Intercom for error reporting
  const errorMessage = `${handleErrorType(
    error
  )}. For further information or guidance, please visit our Knowledge Centre.`;

  toast.error(title, {
    description: description ?? errorMessage,
    dismissible: stay ?? true,
    duration: stay ? 0 : 10000,
    action: {
      label: "Visit",
      onClick: () => {
        window.open("https://kb.jenesys.co");
      }
    }
  });
};

export const toastSuccess = (
  title?: string,
  description?: string,
  stay?: boolean
) => {
  toast.success(title, {
    description,
    dismissible: stay ?? true,
    duration: stay ? 0 : 4000
  });
};

/**
 * Generates custom styles for a select component.
 * @param {string} [size] - The size of the select component. Can be "sm", "md", or "lg".
 * @returns {StylesConfig<any, false, any>} - The custom styles for the select component.
 */
export const customSelectStyles = (
  size?: "sm" | "md" | "lg"
): StylesConfig<any, false, any> => {
  const defaultStyles: StylesConfig<any, false, any> = {
    option: (styles, state) => ({
      ...styles,
      // eslint-disable-next-line no-nested-ternary
      backgroundColor: state.isSelected
        ? theme.colors.primary["500"]
        : state.isFocused
          ? theme.colors.primary["50"]
          : "transparent",
      fontSize: size === "sm" ? 14 : 16,
      fontWeight: 500,
      "&:hover": {
        backgroundColor: state.isSelected
          ? theme.colors.primary["500"]
          : theme.colors.primary["50"]
      }
    }),
    menu: (styles) => ({
      ...styles,
      zIndex: 999
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      padding: "0 8px"
    }),
    placeholder: (styles) => ({
      ...styles,
      whiteSpace: "nowrap"
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      fontSize: "small"
    })
  };

  const defaultControlStyles = {
    fontWeight: 500,
    boxShadow: "none",
    borderColor: "#e0e0e0",
    "&:hover": {
      borderColor: "#6A30D0"
    },
    "&:focus-within": {
      borderColor: "#6A30D0"
    }
  };

  switch (size) {
    case "sm":
      return {
        ...defaultStyles,
        control: (styles) => ({
          ...styles,
          ...defaultControlStyles,
          fontSize: 14,
          height: 34,
          minHeight: 34
        }),
        dropdownIndicator: (styles) => ({
          ...styles,
          padding: "0 4px"
        }),
        clearIndicator: (styles) => ({
          ...styles,
          padding: "0 4px"
        })
      };

    case "md":
      return {
        ...defaultStyles,
        control: (styles) => ({
          ...styles,
          ...defaultControlStyles,
          fontSize: 14,
          height: 40,
          minHeight: 40
        })
      };

    default:
      return {
        ...defaultStyles,
        control: (styles) => ({
          ...styles,
          ...defaultControlStyles,
          height: 50
        })
      };
  }
};

export const bankReconPlatforms: {
  [key: string]: {
    name: string;
    category: string;
    displayName: string;
    imageLink: string;
    description: string;
    isDisabled?: boolean;
    status?: string;
  };
} = {
  yapily: {
    name: "yapily",
    isDisabled: !isFeatureEnabled("allowYapilyIntegration"),
    category: "bank-reconciliation",
    displayName: "Yapily",
    imageLink: "/images/yapily.svg",
    description: "Connect to Open Banking via Yapily.",
    status: "ACTIVE"
  },
  manual: {
    name: "manual",
    isDisabled: false,
    category: "bank-reconciliation",
    displayName: "Manual Banks",
    imageLink: "/images/bank.png",
    description: "Manage manually added bank accounts",
    status: "MANUAL"
  }
};

export const industryOptions = [
  {
    label: "Retail",
    value: "RETAIL"
  },
  {
    label: "E-commerce",
    value: "E_COMMERCE"
  },
  {
    label: "Hospitality",
    value: "HOSPITALITY"
  },
  {
    label: "Construction",
    value: "CONSTRUCTION"
  },
  {
    label: "Professional Services",
    value: "PROFESSIONAL_SERVICES"
  },
  {
    label: "Healthcare",
    value: "HEALTHCARE"
  },
  {
    label: "Technology",
    value: "TECHNOLOGY"
  },
  {
    label: "Manufacturing",
    value: "MANUFACTURING"
  },
  {
    label: "Logistics & Transport",
    value: "LOGISTICS_AND_TRANSPORT"
  },
  {
    label: "Creative & Media",
    value: "CREATIVE_AND_MEDIA"
  },
  {
    label: "Real Estate",
    value: "REAL_ESTATE"
  },
  {
    label: "Education",
    value: "EDUCATION"
  },
  {
    label: "Finance",
    value: "FINANCE"
  },
  {
    label: "Legal",
    value: "LEGAL"
  },
  {
    label: "Charity & Non-profit",
    value: "CHARITY_AND_NON_PROFIT"
  },
  {
    label: "Other",
    value: "OTHER"
  }
];

export const commsPlatforms: {
  [key: string]: {
    name: string;
    category: string;
    displayName: string;
    imageLink: string;
    description: string;
    isDisabled?: boolean;
    helpLink?: string;
    forClients?: boolean;
    forCompanies?: boolean;
    primaryButtonColor?: string;
    secondaryButtonColor?: string;
  };
} = {
  whatsapp: {
    name: "whatsapp",
    category: "comms",
    displayName: "WhatsApp",
    imageLink: "/images/whatsapp.svg",
    helpLink: "https://kb.jenesys.co/whatsapp-connecting-jack-0",
    description:
      "Connect client employee mobile numbers to enable them to send documents directly to Jack via WhatsApp.",
    primaryButtonColor: "#14786d",
    secondaryButtonColor: "#f4f4f4",
    forClients: true,
    forCompanies: true
  },
  slack: {
    name: "slack",
    category: "comms",
    isDisabled: true,
    displayName: "Slack",
    imageLink: "/images/slack.svg",
    forClients: true,
    forCompanies: false,
    description:
      "Connect client employee Slack accounts to enable them to send documents directly to Jack via Slack."
  },
  teams: {
    name: "teams",
    category: "comms",
    isDisabled: true,
    displayName: "Teams",
    imageLink: "/images/teams.svg",
    forClients: true,
    forCompanies: false,
    description:
      "Connect client employee Teams accounts to enable them to send documents directly to Jack via Teams."
  }
};

export const emailPlatforms: {
  [key: string]: {
    name: string;
    category: string;
    displayName: string;
    imageLink: string;
    isDisabled?: boolean;
    description: string;
    helpLink?: string;
    primaryButtonColor?: string;
    secondaryButtonColor?: string;
  };
} = {
  gmail: {
    name: "gmail",
    category: "email",
    displayName: "Gmail",
    imageLink: "/images/gmail.svg",
    description:
      "Connect client employee email accounts, allowing Jack to fetch relevant expense documents directly from Gmail.",
    helpLink: "https://kb.jenesys.co/gmail-fetch",
    primaryButtonColor: "#db4733",
    secondaryButtonColor: "#f3f0f0"
  },
  outlook: {
    name: "outlook",
    category: "email",
    displayName: "Outlook",
    imageLink: "/images/outlook.svg",
    isDisabled: !isFeatureEnabled("allowOutlookIntegration"),
    helpLink: "https://kb.jenesys.co/microsoft-office365-outlook-fetch",
    primaryButtonColor: "#db4733",
    secondaryButtonColor: "#f3f0f0",
    description:
      "Connect client employee email accounts, allowing Jack to fetch relevant expense documents directly from Outlook."
  }
};

export const accountingPlatforms: {
  [key: string]: {
    name: string;
    helpLink?: string;
    category: string;
    imageLink: string;
    isDisabled?: boolean;
    displayName: string;
    description: string;
    tooltip_position?: string;
    tooltip?: string;
    connectImage?: string;
    primaryButtonColor?: string;
    secondaryButtonColor?: string;
    disconnectImage?: string;
  };
} = {
  xero: {
    name: "xero",
    category: "accounting",
    displayName: "Xero",
    isDisabled: !isFeatureEnabled("allowXeroIntegration"),
    imageLink: "/images/xero.svg",
    description: `
    Connect to Xero to enable Jack to fetch
    the Chart of Accounts and Tax Codes and
    publish and reconcile expenses.
  `,
    connectImage: "/images/xero_blue_connect.svg",
    disconnectImage: "/images/xero_white_disconnect.svg"
  },
  sage: {
    name: "sage",
    category: "accounting",
    displayName: "Sage",
    isDisabled: !isFeatureEnabled("allowSageIntegration"),
    imageLink: "/images/sage.svg",
    description: `
      Connect to Sage to enable Jack to fetch
      the Chart of Accounts and Tax Codes and
      publish and reconcile expenses.
    `,
    primaryButtonColor: "#02b231",
    secondaryButtonColor: "#f4f4f4"
  },
  quickbooks: {
    name: "quickbooks",
    category: "accounting",
    displayName: "QuickBooks",
    isDisabled: !isFeatureEnabled("allowQuickbooksIntegration"),
    imageLink: "/images/quickbooks.svg",
    description: `
      Connect to QuickBooks to enable Jack to fetch
      the Chart of Accounts and Tax Codes and publish
      and reconcile expenses.
    `,
    primaryButtonColor: "#2C9F1C",
    secondaryButtonColor: "#f4f4f4"
  },
  free_agent: {
    name: "free_agent",
    category: "accounting",
    displayName: "FreeAgent",
    isDisabled: !isFeatureEnabled("allowFreeAgentIntegration"),
    imageLink: "/images/free-agent.svg",
    description: `
      Connect to FreeAgent to enable Jack to fetch
      the Chart of Accounts and Tax Codes and publish
      and reconcile expenses.
    `,
    primaryButtonColor: "#0069BF",
    secondaryButtonColor: "#f4f4f4"
  }
};

export const accountingDataSynchronizationSteps = {
  store_accounts: "Accounts",
  store_tax_codes: "Tax Codes",
  store_vendors: "Vendors",
  store_invoices: "Invoices",
  store_currencies: "Currencies",
  store_tracking_categories: "Tracking Categories"
};

type AccountingStatus = "in_progress" | "pending" | "failed" | "completed";

export const accountingDataStatusStyles: Record<
  AccountingStatus,
  { text: string; color: string; bg: string; icon: any }
> = {
  in_progress: {
    text: "In Progress",
    color: "orange.600",
    bg: "orange.100",
    icon: PiHourglassSimpleLowFill
  },
  pending: {
    text: "In Progress",
    color: "orange.600",
    bg: "orange.100",
    icon: PiHourglassSimpleLowFill
  },
  failed: {
    text: "Not Completed",
    color: "red.600",
    bg: "red.100",
    icon: TiWarningOutline
  },
  completed: {
    text: "Completed",
    color: "green.600",
    bg: "green.100",
    icon: CiCircleCheck
  }
};

/** This function will check if the timestamp has a timezone character (Z) attached to it.
 * If so, it will return the timestamp as is. Else, it will add the timezone character to the timestamp
 * @param timestamp - The timestamp to be checked
 * @returns The timestamp with the timezone character added
 */
export const addTimeZoneToTimestamp = (timestamp: string) =>
  timestamp.endsWith("Z") ? timestamp : `${timestamp}Z`;

/**
 *
 * @param accountingDataStatus -  The status of the accounting data synchronization.
 * @param activeClient      -  The active client.
 * @param activeCompany    -  The active company.
 * @param canConnectToAccounting  -  A boolean value that indicates whether the user can connect to an accounting platform.
 * @returns
 */
export function getAccountingDataStatusMessage(
  accountingDataStatus: IAccountingDataStatus | undefined,
  activeClientName: string = "Your Client",
  activeCompany: any = "Your Company",
  canConnectToAccounting: boolean = true
) {
  const currentStep = accountingDataStatus
    ? accountingDataSynchronizationSteps[accountingDataStatus.last_step]
    : "Accounting Data";
  if (
    accountingDataStatus?.status === "in_progress" ||
    accountingDataStatus?.status === "pending"
  ) {
    return `Jack is currently pulling ${currentStep} for ${activeClientName}. You will be notified when Jack is ready to receive documents for extraction, review and publish.`;
  }

  if (accountingDataStatus?.status === "failed") {
    return `Unfortunately Jack was unable to pull the ${currentStep} for ${activeClientName}. This could be due to several issues. A member of the Jenesys team has been alerted and will be in contact within 2 working days to resolve the issue with you.`;
  }

  if (accountingDataStatus?.status === "completed") {
    return `Jack has successfully pulled the Chart of Accounts and Tax Codes for ${activeClientName}. Transactions can now be sent to Jack for extraction, review and publishing.`;
  }

  if (canConnectToAccounting) {
    return "You need to connect to an accounting platform to extract documents.";
  }

  return `${activeCompany?.name} has not been connected to an accounting platform yet.`;
}

export interface MappedCategory {
  categoryId: string;
  categoryName: string;
  options: Array<{
    optionId: string;
    name: string;
  }>;
}

/**
 * The function `formatNumberByCommas` takes a number as input and returns a string representation of
 * the number with commas separating every three digits.
 * @param {string | number} number - The `number` parameter in the `formatNumberByCommas` function can
 * be either a string or a number.
 */
export const formatNumberByCommas = (number: string | number): string =>
  number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const formatCompletedAtDateTime = (
  timestamp: string,
  status: string
) => {
  if (status !== "COMPLETED") return null;

  const date = new Date(timestamp);

  let hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();

  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  const formattedMinutes = minutes.toString().padStart(2, "0");

  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const year = date.getUTCFullYear();

  return `${hours}:${formattedMinutes}${period} GMT, ${day}/${month}/${year}`;
};

/**
 * The function `formatDate` takes a date string and returns a formatted date string in the format
 * "dd/mm/yyyy, hh.mmam/pm".
 * @param dateString - The `dateString` parameter is a string that represents a date and time.
 * @returns The function `formatDate` returns a formatted date string in the format "dd/mm/yyyy, hh.mmam/pm".
 */
export const formatDate = (dateString: string) => {
  const date = new Date(dateString);

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  const formattedHours = hours % 12 || 12;

  return `${day}/${month}/${year}, ${formattedHours}.${minutes}${ampm}`;
};

export const getFileSize = (size: number) =>
  // eslint-disable-next-line no-nested-ternary
  size < 1024
    ? `${size} B`
    : size < 1048576
      ? `${(size / 1024).toFixed(2)} KB`
      : `${(size / 1048576).toFixed(2)} MB`;

/**
 * Flattens an array of options and removes duplicates.
 * @param options - The array of options to flatten.
 * @param depth - The depth of the options.
 * @param processedIds - The set of processed options.
 * @returns The flattened array of options.
 */
export function flattenOptionsDedup(
  options: TrackingCategoryOption[],
  depth = 0,
  processedIds = new Set<string>()
): TrackingCategoryNestedSelectOption[] {
  return options.reduce((acc: TrackingCategoryNestedSelectOption[], option) => {
    if (!processedIds.has(option.id)) {
      processedIds.add(option.id);
      acc.push({
        label: option.name,
        value: option.id,
        depth
      });
      if (option.children && option.children.length > 0) {
        acc.push(
          ...flattenOptionsDedup(option.children, depth + 1, processedIds)
        );
      }
    }
    return acc;
  }, []);
}

/**
 * The function takes a date string and returns a formatted date and time string in the format "Jul 08, 2025, 19:31".
 * @param dateString - The `dateString` parameter is a string that represents a date and time.
 * @returns a formatted date and time string in the format "Jul 08, 2025, 19:31".
 */
export const formatDateForQueryTable = (dateString: string) => {
  const date = new Date(dateString);

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric"
  });

  const timeFormatter = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });

  const formattedDate = dateFormatter.format(date);
  const formattedTime = timeFormatter.format(date);

  return { date: formattedDate, time: formattedTime };
};

/**
 * The function takes a string as input and returns a new string with the first letter capitalized and the rest of the letters in lowercase.
 * @param string - The string to be formatted.
 * @returns A new string with the first letter capitalized and the rest of the letters in lowercase.
 */
export const lowercaseAllButFirstLetter = (string: string) =>
  string[0].toUpperCase() + string.slice(1).toLowerCase();
